import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICurrency, IFacility } from "../../types/api";
import {
  MAX_CART_QUANTITY,
  MAX_CARTS_COUNT,
  MAX_ITEMS_EACH_CART_QUANTITY,
} from "../../utils/constants/CommonConstants";
import { RootState } from "../store";
import { IOfferItem } from "./../../types/Offer/index";
import { toast } from "react-toastify";
import i18next from "i18next";
import { SharedCartOrder } from "./sharedCartSlice";

/** ============================= */
/**           Interfaces          */
/** ============================= */

/**
 * Represents a single cart item in an order.
 */
export interface CartItem {
  productId: number;
  offerId: number;
  packageId: number;
  quantity: number;
  price: number;
  image: string | null;
  offerItems?: IOfferItem[];
  name: string;
  nameAr: string;
  nameEn: string;
  itemType: string;
  warehouse: IFacility | undefined;
  currency: ICurrency | undefined;
}

/**
 * Represents an order which can be of type 'ALL' or 'SPECIFIC'.
 */
export interface Order {
  type: "ALL" | "SPECIFIC";
  items: CartItem[];
  totalprice: number;
  warehouse: IFacility | undefined;
}

/**
 * The entire cart state, containing one or more orders and totals.
 */
interface CartState {
  orders: Order[];
  totalCartPrice: number;
  totalItems: number;
  showAddOfferPopup: boolean;
  isCartShared: boolean;
}

/** ============================= */
/**       Initial State           */
/** ============================= */

const initialState: CartState = {
  orders: [],
  totalCartPrice: 0,
  totalItems: 0,
  showAddOfferPopup: false,
  isCartShared: false,
};

/** ============================= */
/**      Utility Functions        */
/** ============================= */

/**
 * Safely convert an Immer proxy (or any value) to a plain JS object/string for logging.
 * If the value is `undefined` or `null`, returns a string instead of throwing an error.
 */
function safeToJS<T>(value: T): T | string {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  return JSON.parse(JSON.stringify(value));
}

/**
 * Finds an existing item in an order by matching productId, offerId, or packageId.
 */
function findExistingItem(
  order: Order,
  newItem: CartItem,
): CartItem | undefined {
  return order.items.find((item) => {
    if (newItem.productId) {
      return item.productId === newItem.productId;
    }
    if (newItem.offerId) {
      return item.offerId === newItem.offerId;
    }
    if (newItem.packageId) {
      return item.packageId === newItem.packageId;
    }
    return false;
  });
}

/**
 * Calculates the total price of all items in a single order.
 */
function calculateOrderTotalprice(order: Order): number {
  const totalPriceForTotalOrdersInCart = order.items.reduce(
    (total, item) => total + item.quantity * item.price,
    0,
  );
  return totalPriceForTotalOrdersInCart;
}

/**
 * Calculates the total number of items across all orders in the cart.
 */

function calculateTotalItems(orders: Order[]): number {
  return orders.reduce((total, order) => total + order.items.length, 0);
}

/** ============================= */
/**         Slice Setup           */
/** ============================= */

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    /**
     * Clears the entire cart, resetting to an empty state.
     */
    clearCart: (state) => {
      state.orders = [];
      state.totalCartPrice = 0;
      state.totalItems = 0;
    },

    /**
     * Adds an item to the cart. If an identical item (by productId / offerId / packageId) exists, increase its quantity.
     */
    addToCart: (
      state,
      action: PayloadAction<{
        productId: number;
        offerId: number;
        packageId: number;
        quantity: number;
        price: number;
        image: string | null;
        offerItems?: unknown;
        name: string;
        nameAr: string;
        nameEn: string;
        warehouse: IFacility | undefined;
        itemType: string;
        currency: ICurrency | undefined;
      }>
    ) => {
      const {
        productId,
        offerId,
        packageId,
        quantity,
        price,
        image,
        name,
        nameAr,
        nameEn,
        offerItems,
        warehouse,
        itemType,
        currency,
      } = action.payload;

      const orderType: "ALL" | "SPECIFIC" = warehouse ? "SPECIFIC" : "ALL";

      // Find existing cart
      let order = warehouse
        ? state.orders.find(
          (o) => o?.warehouse?.Id === warehouse.Id
        )
        : state.orders.find((o) => o.type === "ALL");

      // Create cart if not exists
      if (!order) {
        // Max carts validation
        if (state.orders.length >= MAX_CARTS_COUNT) {
          toast.warning(i18next.t("tooManyOrders"), {
            closeOnClick: true,
          });
          return;
        }

        order = {
          type: orderType,
          items: [],
          totalprice: 0,
          warehouse,
        };

        state.orders.push(order);
      }

      const newItem: CartItem = {
        productId,
        offerId,
        packageId,
        quantity,
        price,
        image,
        offerItems: Array.isArray(offerItems) ? offerItems : [],
        name,
        nameAr,
        nameEn,
        itemType,
        warehouse,
        currency,
      };
      const existingItem = findExistingItem(order, newItem);

      // Existing item
      if (existingItem) {
        existingItem.quantity = Math.min(
          existingItem.quantity + quantity,
          MAX_CART_QUANTITY
        );
      } else {
        // Max items per cart validation
        if (order.items.length >= MAX_ITEMS_EACH_CART_QUANTITY) {
          toast.warning(i18next.t("maxItemsPerCart"), {
            closeOnClick: true,
          });
          return;
        }

        order.items.push({
          ...newItem,
          quantity: Math.min(quantity, MAX_CART_QUANTITY),
        });
      }

      // Recalculate totals
      order.totalprice = calculateOrderTotalprice(order);

      state.totalCartPrice = state.orders.reduce(
        (total, currentOrder) => total + currentOrder.totalprice,
        0
      );

      state.totalItems = calculateTotalItems(state.orders);
    },
    /**
     * Removes an item from the cart, by matching productId/offerId/packageId on a target order.
     */
    removeFromCart: (
      state,
      action: PayloadAction<{
        productId?: number;
        offerId?: number;
        packageId?: number;
        warehouse?: IFacility;
      }>,
    ) => {
      const { productId, offerId, packageId, warehouse } = action.payload;

      // 1. Find the order to remove from
      const targetOrder = warehouse
        ? state.orders.find((order) => order.warehouse?.Id === warehouse?.Id)
        : state.orders.find((order) => order.type === "ALL");
      // 2. If found, remove the matching item(s)
      const isMatchingItem = (item: CartItem) => {
        const matchProduct =
          productId !== undefined
            ? item.productId === productId ||
            (item.productId === null && productId === 0)
            : true;

        const matchOffer =
          offerId !== undefined
            ? item.offerId === offerId ||
            (item.offerId === null && offerId === 0)
            : true;

        const matchPackage =
          packageId !== undefined
            ? item.packageId === packageId ||
            (item.packageId === null && packageId === 0)
            : true;

        return matchProduct && matchOffer && matchPackage;
      };

      if (targetOrder) {
        targetOrder.items = targetOrder.items.filter(
          (item) => !isMatchingItem(item),
        );

        // 3. Recalculate target order total
        targetOrder.totalprice = calculateOrderTotalprice(targetOrder);

        // 4. If no items left in this order, remove the entire order
        if (targetOrder.items.length === 0) {
          state.orders = state.orders.filter((order) => order !== targetOrder);
        }

        // 5. Recalculate overall cart totals
        state.totalCartPrice = state.orders.reduce(
          (total, order) => total + order.totalprice,
          0,
        );
        state.totalItems = calculateTotalItems(state.orders);
      }
    },

    /**
     * Decreases the quantity of a specific item by `quantity`; if it goes below 1, remove it entirely.
     */
    decreaseQuantity: (
      state,
      action: PayloadAction<{
        productId?: number;
        offerId?: number;
        packageId?: number;
        quantity: number;
        warehouse?: IFacility;
      }>,
    ) => {
      const { productId, offerId, packageId, quantity, warehouse } =
        action.payload;

      // 1. Locate the target order
      const targetOrder = warehouse
        ? state.orders.find((order) => order.warehouse?.Id === warehouse?.Id)
        : state.orders.find((order) => order.type === "ALL");
      // 2. If found, find the item in this order

      if (targetOrder) {
        const existingItem = targetOrder.items.find(
          (item) =>
            (item.productId === productId ||
              (item.productId === null && productId === 0)) &&
            (item.offerId === offerId ||
              (item.offerId === null && offerId === 0)) &&
            (item.packageId === packageId ||
              (item.packageId === null && packageId === 0)),
        );

        // 3. Decrease quantity if the item exists
        if (existingItem) {
          existingItem.quantity -= quantity;

          // 4. Remove item entirely if <= 0
          if (existingItem.quantity <= 0) {
            targetOrder.items = targetOrder.items.filter(
              (item) =>
                item.productId !== productId ||
                item.offerId !== offerId ||
                item.packageId !== packageId,
            );
          }

          // 5. Recalculate the target order total
          targetOrder.totalprice = calculateOrderTotalprice(targetOrder);

          // 6. Remove the order if empty
          if (targetOrder.items.length === 0) {
            state.orders = state.orders.filter(
              (order) => order !== targetOrder,
            );
          }

          // 7. Recalculate overall cart totals
          state.totalCartPrice = state.orders.reduce(
            (total, order) => total + order.totalprice,
            0,
          );
          state.totalItems = calculateTotalItems(state.orders);
        }
      }
    },
    showAddOfferConfirmationPopup: (state, action: PayloadAction<boolean>) => {
      state.showAddOfferPopup = action.payload;
    },
    toggleIsSharedCartStatus: (state, action: PayloadAction<boolean>) => {
      state.isCartShared = action.payload;
    },
    setSharedCartResponseToOriginalCartFromApi: (state, action) => {
      clearCart();

      state.orders = action.payload;

      state.totalCartPrice = state.orders.reduce(
        (sum, o) => sum + o.totalprice,
        0,
      );

      state.totalItems = calculateTotalItems(state.orders);
    },
  },
});

/** ============================= */
/**       Selectors/Exports       */
/** ============================= */

/**
 * Selector to get the current quantity of a specific item by IDs.
 */
export const getItemQuantityById = (
  state: RootState,
  productId: number,
  offerId: number,
  packageId: number,
): number => {
  for (const order of state.cart.orders) {
    const item = order.items.find(
      (it: CartItem) =>
        it.productId === productId &&
        it.offerId === offerId &&
        it.packageId === packageId,
    );
    if (item) {
      return item.quantity;
    }
  }
  return 0;
};

export const {
  clearCart,
  addToCart,
  removeFromCart,
  decreaseQuantity,
  showAddOfferConfirmationPopup,
  toggleIsSharedCartStatus,
  setSharedCartResponseToOriginalCartFromApi,
} = cartSlice.actions;

export default cartSlice.reducer;
